import { computed, shallowRef, type ComputedRef, type Ref } from 'vue';

/** Effective visual state of a single step. */
export type StepStatus = 'done' | 'active' | 'open';

export interface StepperRegistry {
  /** Register a step by stable identity; returns an unregister fn. Idempotent. */
  register: (id: symbol) => () => void;
  /** Reactive zero-based position of a step in registration (DOM) order; -1 once gone. */
  indexOf: (id: symbol) => ComputedRef<number>;
  /** Reactive number of registered steps. */
  count: ComputedRef<number>;
  /** Status of the step at `index`, derived from the active index. */
  statusFor: (index: number) => StepStatus;
  /**
   * Whether `index` may be navigated to: opt-in `clickable`, and while `linear`
   * only done/active steps (no skipping ahead); not linear, any registered step.
   */
  canNavigate: (index: number) => boolean;
  /** Navigate to `index` iff `canNavigate(index)`; delegates the write to `onNavigate`. */
  goTo: (index: number) => void;
}

/**
 * Ordering / status / navigation logic for a stepper, kept free of any
 * component so it can be unit-tested without mounting. The active index is
 * owned by the caller (`NxStepper`'s model): this registry never writes it
 * directly, it signals every change through `onNavigate`.
 *
 * `id`s are held in a `shallowRef` array in registration order — for the static
 * slot children a stepper takes, that order matches the DOM.
 *
 * `linear` (default true) keeps navigation to steps up to the active one; set to
 * false, every registered step can be jumped to. Status is unaffected either way.
 */
export function createStepperRegistry(opts: {
  current: Ref<number>;
  clickable: Ref<boolean>;
  linear?: Ref<boolean>;
  onNavigate: (index: number) => void;
}): StepperRegistry {
  const { current, clickable, linear, onNavigate } = opts;
  const ids = shallowRef<symbol[]>([]);

  function register(id: symbol): () => void {
    if (!ids.value.includes(id)) ids.value = [...ids.value, id];
    return () => {
      ids.value = ids.value.filter((x) => x !== id);
    };
  }

  function indexOf(id: symbol): ComputedRef<number> {
    return computed(() => ids.value.indexOf(id));
  }

  const count = computed(() => ids.value.length);

  function statusFor(index: number): StepStatus {
    if (current.value > index) return 'done';
    if (current.value === index) return 'active';
    return 'open';
  }

  function canNavigate(index: number): boolean {
    if (!clickable.value || index < 0) return false;
    if (linear?.value ?? true) return index <= current.value;
    return index < ids.value.length;
  }

  function goTo(index: number): void {
    if (canNavigate(index)) onNavigate(index);
  }

  return { register, indexOf, count, statusFor, canNavigate, goTo };
}
